import { 
  collection, 
  doc, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  deleteDoc,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { logger } from '../utils/logger';

export interface Document {
  id: string;
  customerId: string;
  customerEmail: string;
  customerName: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  downloadUrl: string;
  uploadedAt: Date;
  description?: string;
}

// Upload a document to Firebase Storage and save metadata to Firestore
export const uploadDocument = async (
  file: File,
  customerId: string,
  customerEmail: string,
  customerName: string,
  description?: string
): Promise<Document> => {
  try {
    logger.log('📤 Uploading document:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      customerId
    });

    // Validate file type (PDF preferred, but allow other types)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not supported. Please upload PDF, images, or Word documents.');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 10MB.');
    }

    // Create storage path: documents/{customerId}/{timestamp}-{filename}
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `documents/${customerId}/${timestamp}-${sanitizedFileName}`;
    const storageRef = ref(storage, storagePath);

    // Upload file to Firebase Storage
    logger.log('📤 Uploading to storage:', storagePath);
    await uploadBytes(storageRef, file);
    logger.log('✅ File uploaded to storage');

    // Get download URL
    const downloadUrl = await getDownloadURL(storageRef);
    logger.log('✅ Download URL obtained:', downloadUrl);

    // Save document metadata to Firestore
    const documentData = {
      customerId,
      customerEmail,
      customerName,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      storagePath,
      downloadUrl,
      description: description || '',
      uploadedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'documents'), documentData);
    logger.log('✅ Document metadata saved to Firestore:', docRef.id);

    return {
      id: docRef.id,
      ...documentData,
      uploadedAt: new Date()
    };
  } catch (error: any) {
    logger.error('❌ Error uploading document:', error);
    throw error;
  }
};

// Get all documents for a specific customer
export const getCustomerDocuments = async (customerId: string): Promise<Document[]> => {
  try {
    logger.log('🔍 Getting documents for customer:', customerId);
    
    // Query without orderBy to avoid index requirement, then sort in JavaScript
    const documentsQuery = query(
      collection(db, 'documents'),
      where('customerId', '==', customerId)
    );
    
    const snapshot = await getDocs(documentsQuery);
    
    logger.log('📊 Documents snapshot size:', snapshot.docs.length);
    
    let documents = snapshot.docs.map(doc => {
      const data = doc.data();
      logger.log('📄 Document data:', {
        id: doc.id,
        fileName: data.fileName,
        customerId: data.customerId,
        uploadedAt: data.uploadedAt
      });
      return {
        id: doc.id,
        ...data,
        uploadedAt: data.uploadedAt?.toDate ? data.uploadedAt.toDate() : new Date()
      };
    }) as Document[];
    
    // If no documents found with direct query, try getting all and filtering (fallback)
    if (documents.length === 0) {
      logger.log('⚠️ No documents found with direct query, trying fallback: get all documents');
      try {
        const allDocsQuery = query(collection(db, 'documents'));
        const allSnapshot = await getDocs(allDocsQuery);
        logger.log('📊 All documents in collection:', allSnapshot.docs.length);
        
        documents = allSnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              uploadedAt: data.uploadedAt?.toDate ? data.uploadedAt.toDate() : new Date()
            } as Document;
          })
          .filter(doc => doc.customerId === customerId);
        
        logger.log('📄 Filtered documents for customer:', documents.length);
        logger.log('📄 All document customerIds found:', allSnapshot.docs.map(d => ({ 
          fileName: d.data().fileName, 
          customerId: d.data().customerId 
        })));
      } catch (fallbackError) {
        logger.error('❌ Fallback query also failed:', fallbackError);
      }
    }
    
    // Sort by uploadedAt (newest first)
    documents.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
    
    logger.log('✅ Found documents:', documents.length);
    logger.log('📄 Document customerIds:', documents.map(d => ({ fileName: d.fileName, customerId: d.customerId })));
    return documents;
  } catch (error) {
    logger.error('❌ Error getting customer documents:', error);
    // Try fallback: get all documents and filter
    try {
      logger.log('🔄 Trying fallback: get all documents');
      const allDocsQuery = query(collection(db, 'documents'));
      const allSnapshot = await getDocs(allDocsQuery);
      const documents = allSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            uploadedAt: data.uploadedAt?.toDate ? data.uploadedAt.toDate() : new Date()
          } as Document;
        })
        .filter(doc => doc.customerId === customerId);
      documents.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
      logger.log('✅ Fallback found documents:', documents.length);
      return documents;
    } catch (fallbackError) {
      logger.error('❌ Fallback also failed:', fallbackError);
      throw error;
    }
  }
};

// Get all documents (for admin)
export const getAllDocuments = async (): Promise<Document[]> => {
  try {
    logger.log('🔍 Getting all documents...');
    const documentsQuery = query(
      collection(db, 'documents'),
      orderBy('uploadedAt', 'desc')
    );
    const snapshot = await getDocs(documentsQuery);
    
    const documents = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      uploadedAt: doc.data().uploadedAt?.toDate ? doc.data().uploadedAt.toDate() : new Date()
    })) as Document[];
    
    logger.log('✅ Found documents:', documents.length);
    return documents;
  } catch (error) {
    logger.error('❌ Error getting all documents:', error);
    throw error;
  }
};

// Delete a document
export const deleteDocument = async (documentId: string, storagePath: string): Promise<void> => {
  try {
    logger.log('🗑️ Deleting document:', documentId);
    
    // Delete from Firestore
    await deleteDoc(doc(db, 'documents', documentId));
    
    // Delete from Storage
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    
    logger.log('✅ Document deleted successfully');
  } catch (error) {
    logger.error('❌ Error deleting document:', error);
    throw error;
  }
};

// Format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

